<app-ai-document-processing-disclaimer
	[matterDocumentType]="matterDocumentType"
></app-ai-document-processing-disclaimer>

<app-container
	title="Details"
	orientation="col"
	[id]="COMMISSION_DETAILS_FRAGMENT.DETAILS"
	[testHook]="testHooks.DetailsContainer"
>
	<div class="flex flex-col gap-5 flex-1 border-t border-grey-light">
		<app-sale-saleprice class="mt-3" label="Sale Price"></app-sale-saleprice>

		<app-radio-group
			label="Type"
			labelOrientation="row"
			[labelToolTip]="aiCommissionsTypeTooltip$ | async"
			[labelIcon]="AI_ICON"
			[labelIconClasses]="AI_ICON_CLASSES"
			[items]="commissionTypeOptions"
			[ngModel]="(commissionSettings$ | async)?.type"
			(ngModelChange)="onCommTypeChange($event)"
			type="compact"
			[skeletonLoading]="aiScrapingLoading$ | async"
			[testHook]="testHooks.TypeRadioGroup"
		>
		</app-radio-group>

		<app-input
			label="Percentage"
			orientation="row"
			textAlign="right"
			type="text"
			[labelToolTip]="aiCommissionsValueTooltip$ | async"
			[labelIcon]="AI_ICON"
			[labelIconClasses]="AI_ICON_CLASSES"
			[disabled]="
				(commissionSettings$ | async)?.type !== commissionType.PERCENTAGE
			"
			[endIcon]="ICON.PERCENTAGE"
			[ngModel]="(commissionCalculated$ | async)?.percentage | number: '1.0-2'"
			(debounce)="onPercentChange($event)"
			[skeletonLoading]="aiScrapingLoading$ | async"
			[testHook]="testHooks.Percentage"
		>
		</app-input>

		<app-input
			label="Commission Payable"
			type="cents"
			textAlign="right"
			orientation="row"
			[labelToolTip]="aiCommissionsValueTooltip$ | async"
			[labelIcon]="AI_ICON"
			[labelIconClasses]="AI_ICON_CLASSES"
			[disabled]="
				(commissionSettings$ | async)?.type !== commissionType.FLAT_FEE
			"
			[startIcon]="ICON.DOLLAR"
			[ngModel]="(commissionCalculated$ | async)?.payable"
			(debounce)="onCommissionPayableChange($event)"
			[skeletonLoading]="aiScrapingLoading$ | async"
			[testHook]="testHooks.CommissionPayable"
		>
		</app-input>

		<div
			class="flex justify-between mb-3"
			*ngIf="!(aiScrapingLoading$ | async); else amountSkeleton"
		>
			<div class="flex gap-3">
				<app-checkbox
					type="clear"
					label="Apply HST ({{ hstPercentage$ | async | percent }})"
					[ngModel]="(commissionSettings$ | async)?.applyHST"
					(ngModelChange)="onApplyHstChange($event)"
					[skeletonLoading]="aiScrapingLoading$ | async"
					[testHook]="testHooks.ApplyHstCheckbox"
				>
				</app-checkbox>
				<app-icon
					*ngIf="
						aiCommissionsApplyHSTTooltip$ | async as aiGeneratedToolTipText
					"
					[icon]="AI_ICON"
					[matTooltip]="aiGeneratedToolTipText"
					[classes]="AI_ICON_CLASSES"
				></app-icon>
			</div>
			<div>
				{{ (commissionCalculated$ | async)?.hstAmount | centsToDollars }}
			</div>
		</div>

		<div
			class="flex justify-between"
			*ngIf="!(aiScrapingLoading$ | async); else amountSkeleton"
		>
			<div class="flex gap-2">
				<span>Commission Total</span>
				<app-icon
					*ngIf="aiCommissionsTotalTooltip$ | async as aiGeneratedToolTipText"
					[icon]="AI_ICON"
					[matTooltip]="aiGeneratedToolTipText"
					[classes]="AI_ICON_CLASSES"
				></app-icon>
			</div>
			<div>
				{{ (commissionCalculated$ | async)?.grandTotal | centsToDollars }}
			</div>
		</div>

		<div
			class="relative py-3"
			(mouseover)="showHideDepositEditControl(true)"
			(mouseleave)="showHideDepositEditControl(false)"
			*ngIf="!(aiScrapingLoading$ | async); else amountSkeleton"
		>
			<div class="flex justify-between items-center w-full h-full">
				<ng-container *ngIf="selectedBrokerageIsDepositHolder$ | async">
					<div>
						Less Deposit Held by {{ depositHolderDisplayName$ | async }}
					</div>
					<div>{{ (depositTotal$ | async)?.absAmount | centsToDollars }}</div>
				</ng-container>

				<ng-container *ngIf="!(selectedBrokerageIsDepositHolder$ | async)">
					<div>Deposit Held by</div>
					<div>{{ depositHolderDisplayName$ | async }}</div>
				</ng-container>
			</div>

			<div
				[@fade]
				class="absolute top-0 left-0 w-full h-full rounded-xl bg-grey-veryLight bg-opacity-80 z-nuclear border border-green-light"
				*ngIf="showDepositEditControl$ | async"
			>
				<div class="flex justify-center items-center w-full h-full">
					<app-button
						[icon]="ICON.EDIT"
						(onClick)="openDepositDrawer()"
						[testHook]="testHooks.EditButton"
					></app-button>
				</div>
			</div>
		</div>

		<app-checkbox
			*ngIf="selectedBrokerageIsDepositHolder$ | async"
			class="mb-3"
			label="Apply Deposit to Commission"
			type="clear"
			[ngModel]="(commissionSettings$ | async)?.applyDeposit"
			(ngModelChange)="onApplyDepositToCommissionChange($event)"
			[testHook]="testHooks.ApplyDepositToCommissionCheckbox"
			[skeletonLoading]="aiScrapingLoading$ | async"
		>
		</app-checkbox>

		<div
			class="flex justify-between"
			*ngIf="!(aiScrapingLoading$ | async); else amountSkeleton"
		>
			<div
				*ngIf="(commissionCalculated$ | async)?.balanceOwing >= 0"
				class="font-bold"
			>
				Balance Owing to {{ brokerageTypeDisplayName }} Brokerage
			</div>
			<div
				*ngIf="(commissionCalculated$ | async)?.balanceOwing < 0"
				class="font-bold"
			>
				Excess Deposit Held by {{ brokerageTypeDisplayName }} Brokerage
			</div>
			<div class="font-bold" [attr.data-intercom-target]="intercomAttrValue">
				{{
					(commissionCalculated$ | async)?.balanceOwing ?? 0
						| absoluteValue
						| centsToDollars
				}}
			</div>
		</div>

		<div
			[@openClose]
			class="border-t border-grey-light"
			*ngIf="
				(selectedBrokerageIsDepositHolder$ | async) &&
				(commissionCalculated$ | async)?.balanceOwing < 0
			"
		>
			<app-radio-group
				class="excess-deposit-recipient-radio mt-3"
				label="Excess Deposit Received by"
				labelToolTip="If Seller is selected, excess deposit will be paid directly to Seller whereas if Seller Lawyer is selected, excess deposit will be directed to trust."
				labelOrientation="row"
				[items]="excessDepositRecipientOptions"
				[ngModel]="(commissionSettings$ | async)?.excessDepositRecipient"
				(ngModelChange)="onExcessDepositRecipientChange($event)"
				type="compact"
				[skeletonLoading]="aiScrapingLoading$ | async"
				[testHook]="testHooks.ExcessDepositReceivedByRadioGroup"
			>
			</app-radio-group>
		</div>
	</div>
</app-container>

<app-deposit-drawer
	#depositDrawer
	(depositHolderSelectionChange)="onDepositHolderSelectionChange($event)"
></app-deposit-drawer>

<ng-template #amountSkeleton>
	<ui-skeleton-loader class="w-full" type="field"></ui-skeleton-loader>
</ng-template>
